import { useState, useEffect, useCallback } from 'react'
import { toast } from 'sonner'
import api from '@/services/api'
import { getFriendlyError } from '@/utils/getFriendlyError'
import {
  parseLeadSourceTotalCost,
  type LeadSourceFormValues,
} from '@/components/Configuracao/leadSourceConstants'

export type LeadSourceRow = {
  id: string
  name: string
  description: string | null
  type: string
  totalCost: number | null
  active: boolean
  leadCount: number
  conversionRate: number
}

function buildCreatePayload(values: LeadSourceFormValues) {
  return {
    name: values.name.trim(),
    type: values.type,
    description: values.description.trim() || null,
    totalCost: parseLeadSourceTotalCost(values.totalCost),
    isActive: values.isActive,
  }
}

export function useLeadSourcesTab(isAdmin: boolean) {
  const [sources, setSources] = useState<LeadSourceRow[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)

  const fetchSources = useCallback(() => {
    setLoading(true)
    api
      .get('/lead-sources')
      .then((resp) => {
        setSources(resp.data.data ?? [])
      })
      .catch((err) => {
        setSources([])
        toast.error(getFriendlyError(err, 'Erro ao carregar origens de lead'))
      })
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    fetchSources()
  }, [fetchSources])

  const createSource = async (values: LeadSourceFormValues) => {
    if (!isAdmin) return false
    setSaving(true)
    try {
      const { data } = await api.post('/lead-sources', buildCreatePayload(values))
      const row = data.data as LeadSourceRow
      setSources((prev) => [...prev, row].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')))
      toast.success('Origem cadastrada')
      setShowCreateModal(false)
      return true
    } catch (err) {
      toast.error(getFriendlyError(err, 'Erro ao cadastrar origem'))
      return false
    } finally {
      setSaving(false)
    }
  }

  const renameSource = async (id: string, name: string) => {
    if (!isAdmin) return false
    const trimmed = name.trim()
    if (!trimmed) return false
    try {
      const { data } = await api.patch(`/lead-sources/${id}`, { name: trimmed })
      const row = data.data as LeadSourceRow
      setSources((prev) =>
        prev.map((s) => (s.id === id ? row : s)).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
      )
      return true
    } catch (err) {
      toast.error(getFriendlyError(err, 'Erro ao renomear origem'))
      return false
    }
  }

  const toggleActive = async (id: string) => {
    if (!isAdmin) return
    const current = sources.find((s) => s.id === id)
    if (!current) return
    try {
      const { data } = await api.patch(`/lead-sources/${id}`, { isActive: !current.active })
      const row = data.data as LeadSourceRow
      setSources((prev) => prev.map((s) => (s.id === id ? row : s)))
    } catch (err) {
      toast.error(getFriendlyError(err, 'Erro ao alterar status da origem'))
    }
  }

  return {
    sources,
    loading,
    saving,
    showCreateModal,
    setShowCreateModal,
    createSource,
    renameSource,
    toggleActive,
    fetchSources,
  }
}
